import { useEffect, useMemo, useRef } from "react"
import { useDispatch } from "react-redux"
import { AppDispatch } from "@/store"
import {
   assignWearableToUnknownLocation,
   hideRemovedWearable,
   resolveWearableLocations,
} from "@/store/unknownLocationWearablesSlice"
import {
   PING_INTERVAL_MS,
   realtimeFeedReceived,
   realtimeFeedsReceived,
   realtimeStatusChanged,
} from "@/store/websocketSlice"
import { normalizeMacAddress } from "@/utils/index"

const RECONNECT_BASE_DELAY_MS = 1000
const RECONNECT_MAX_DELAY_MS = 30000
const FEED_FLUSH_INTERVAL_MS = 100

export enum RealtimeEvent {
   AUTH = "auth",
   FILTER = "filter",
   FEED = "feed",
   PING = "ping",
   NOTICE = "notice",
   WEARABLE_REMOVED = "wearable_removed",
   WEARABLE_ASSIGNED = "wearable_assigned",
}

export type RealtimeProducer = {
   /** ID of the local entity (cell, wearable, …) this sensor maps to. */
   entityId: string
   macAddress: string
   customIdentifier?: string
}

export type RealtimeStatusPayload = {
   connected?: boolean
   authenticated?: boolean
   lastPongAt?: number | null
   lastError?: string | null
}

export type RealtimeFeedPayload = {
   entityId: string
   source: { macAddress: string; customIdentifier?: string }
   rawPayload: unknown
   timestamp: number
}

// Not in use
export type StaticSensorData = {
   battery?: number
   presence?: number
   hr?: number
   hrConfidence?: number
   br?: number
   brConfidence?: number
   spo2?: number
   spo2Confidence?: number
}

export type RealtimeConnectionOptions = {
   url?: string
   rtsToken: string | null | undefined
   producers: RealtimeProducer[]
}

export enum ProducerType {
   STATIC = "static",
   WEARABLE_POSITIONS = "wearable-positions",
   WEARABLE_VITALS = "wearable-vitals",
   WEARABLE_UNASSIGNED = "wearable-unassigned",
   DOOR = "door",
}

type WsAuthPayload = {
   status?: string
}

export type WsInmateSummary = {
   id?: string
   external_id?: string | null
   first_name?: string
   middle_name?: string | null
   last_name?: string
   custom_identifier?: string
   booking_number?: string | null
}

export type WsFeedPayload = {
   source: [string, string?]
   producer_type: ProducerType
   data?: Record<string, unknown>
   control?: {
      vacant?: boolean
      maintenance?: boolean
      hasCustomConfigurations?: boolean
      inmate?: WsInmateSummary
      level?: string
   }
}

type WsNoticePayload = {
   message?: string
}

export type WsWearableRemovedPayload = {
   source: [string, string?]
   // null specifically for an explicit unassignment (producer_type
   // "wearable-unassigned"); otherwise the old/stale anchor the wearable moved off of.
   producer: [string, string?] | null
   producer_type:
      | ProducerType.WEARABLE_POSITIONS
      | ProducerType.WEARABLE_VITALS
      | ProducerType.WEARABLE_UNASSIGNED
}

export type WsWearableAssignedControl = {
   inmate?: WsInmateSummary | null
}

export type WsWearableAssignedPayload = {
   source: [string, string?] // [macAddress, facilityId]
   control: WsWearableAssignedControl | null
}

type WsMessage = {
   event: RealtimeEvent
   payload:
      | WsAuthPayload
      | WsFeedPayload
      | WsNoticePayload
      | WsWearableRemovedPayload
      | WsWearableAssignedPayload
   timestamp: number
}

const sourceKey = (macAddress: string, customIdentifier: string) =>
   `${normalizeMacAddress(macAddress)}::${customIdentifier}`

const now = () => Date.now()

const feedCoalesceKey = (feed: RealtimeFeedPayload): string => {
   const producerType = (feed.rawPayload as WsFeedPayload | undefined)
      ?.producer_type
   return producerType
      ? `${feed.entityId}::${producerType}`
      : `${feed.entityId}`
}

export function useRealtimeConnection({
   url = process.env.REALTIME_WS_URL,
   rtsToken,
   producers,
}: RealtimeConnectionOptions): void {
   const dispatch = useDispatch<AppDispatch>()

   const sourceToIdRef = useRef(new Map<string, string>())
   const authenticatedRef = useRef(false)
   const sendFilterRef = useRef<(() => void) | null>(null)
   const producersRef = useRef<RealtimeProducer[]>(producers)
   const lastFilterKeyRef = useRef<string>("")

   useEffect(() => {
      if (!rtsToken || !url) return

      let socket: WebSocket | null = null
      let reconnectTimer: ReturnType<typeof setTimeout> | null = null
      let pingTimer: ReturnType<typeof setInterval> | null = null
      let feedFlushTimer: ReturnType<typeof setTimeout> | null = null
      let queuedFeeds: RealtimeFeedPayload[] = []
      let reconnectAttempt = 0
      let manuallyClosed = false

      const updateStatus = (patch: RealtimeStatusPayload) => {
         dispatch(realtimeStatusChanged(patch))
      }

      const sendMessage = (event: RealtimeEvent, payload: object) => {
         if (socket?.readyState !== WebSocket.OPEN) return
         socket.send(JSON.stringify({ event, payload, timestamp: now() }))
      }

      const sendFilter = () => {
         if (!authenticatedRef.current) return
         const payload = producersRef.current.map(
            ({ macAddress, customIdentifier }) => ({
               macAddress,
               customIdentifier,
            })
         )
         sendMessage(RealtimeEvent.FILTER, payload)
      }

      sendFilterRef.current = sendFilter

      const sendAuth = () =>
         sendMessage(RealtimeEvent.AUTH, { session: rtsToken })

      const clearReconnectTimer = () => {
         if (reconnectTimer) {
            clearTimeout(reconnectTimer)
            reconnectTimer = null
         }
      }

      const clearPingTimer = () => {
         if (pingTimer) {
            clearInterval(pingTimer)
            pingTimer = null
         }
      }

      const clearFeedFlushTimer = () => {
         if (feedFlushTimer) {
            clearTimeout(feedFlushTimer)
            feedFlushTimer = null
         }
      }

      const flushFeeds = () => {
         clearFeedFlushTimer()
         if (!queuedFeeds.length) return

         const latestByKey = new Map<string, RealtimeFeedPayload>()
         for (const feed of queuedFeeds) {
            latestByKey.set(feedCoalesceKey(feed), feed)
         }

         const nextFeeds = [...latestByKey.values()].sort(
            (aFeed, bFeed) => aFeed.timestamp - bFeed.timestamp
         )
         queuedFeeds = []

         if (nextFeeds.length === 1) {
            dispatch(realtimeFeedReceived(nextFeeds[0]))
         } else {
            dispatch(realtimeFeedsReceived(nextFeeds))
         }

         dispatch(resolveWearableLocations(nextFeeds))
      }

      const scheduleFeedFlush = () => {
         if (feedFlushTimer) return
         feedFlushTimer = setTimeout(flushFeeds, FEED_FLUSH_INTERVAL_MS)
      }

      const startPingInterval = () => {
         clearPingTimer()
         pingTimer = setInterval(
            () => sendMessage(RealtimeEvent.PING, {}),
            PING_INTERVAL_MS
         )
      }

      const closeSocket = () => {
         clearPingTimer()
         clearFeedFlushTimer()
         if (socket) {
            socket.onopen = null
            socket.onmessage = null
            socket.onerror = null
            socket.onclose = null
            if (
               socket.readyState === WebSocket.OPEN ||
               socket.readyState === WebSocket.CONNECTING
            ) {
               socket.close()
            }
            socket = null
         }
      }

      const handleAuthMessage = (payload: WsAuthPayload) => {
         authenticatedRef.current = payload?.status === "ok"
         updateStatus({
            authenticated: authenticatedRef.current,
            lastError: null,
         })
         if (!authenticatedRef.current) return
         sendFilter()
         // Get a fresh pong now rather than at the next interval tick.
         sendMessage(RealtimeEvent.PING, {})
      }

      const handleFeedMessage = (payload: WsFeedPayload) => {
         const [macAddress, customIdentifier] = payload.source

         const isWearable =
            payload.producer_type === ProducerType.WEARABLE_POSITIONS ||
            payload.producer_type === ProducerType.WEARABLE_VITALS

         const isDoorSensor = payload.producer_type === ProducerType.DOOR

         let entityId: string | undefined

         if (isDoorSensor) {
            entityId = payload.control?.level
         } else if (isWearable) {
            entityId = normalizeMacAddress(macAddress)
         } else {
            entityId =
               (customIdentifier &&
                  sourceToIdRef.current.get(
                     sourceKey(macAddress, customIdentifier)
                  )) ||
               sourceToIdRef.current.get(normalizeMacAddress(macAddress))
         }

         if (!entityId) {
            return
         }

         const feedPayload: RealtimeFeedPayload = {
            entityId,
            source: { macAddress, customIdentifier },
            rawPayload: payload,
            timestamp: now(),
         }

         queuedFeeds.push(feedPayload)
         scheduleFeedFlush()
      }

      const handleNoticeMessage = (payload: WsNoticePayload) => {
         updateStatus({ lastError: payload?.message ?? "Realtime notice" })
      }

      const handleWearableRemovedMessage = (
         payload: WsWearableRemovedPayload
      ) => {
         const [macAddress] = payload.source
         const entityId = normalizeMacAddress(macAddress)
         dispatch(hideRemovedWearable(entityId))
      }

      const handleWearableAssignedMessage = (
         payload: WsWearableAssignedPayload
      ) => {
         const [macAddress] = payload.source
         dispatch(
            assignWearableToUnknownLocation({
               macAddress,
               inmate: payload.control?.inmate,
            })
         )
      }

      const handleMessage = (msgEvent: MessageEvent) => {
         try {
            const msg = JSON.parse(msgEvent.data) as WsMessage
            switch (msg.event) {
               case RealtimeEvent.AUTH:
                  handleAuthMessage(msg.payload as WsAuthPayload)
                  break
               case RealtimeEvent.FEED:
                  handleFeedMessage(msg.payload as WsFeedPayload)
                  break
               case RealtimeEvent.PING:
                  dispatch(realtimeStatusChanged({ lastPongAt: now() }))
                  break
               case RealtimeEvent.NOTICE:
                  handleNoticeMessage(msg.payload as WsNoticePayload)
                  break
               case RealtimeEvent.WEARABLE_REMOVED:
                  handleWearableRemovedMessage(
                     msg.payload as WsWearableRemovedPayload
                  )
                  break
               case RealtimeEvent.WEARABLE_ASSIGNED:
                  handleWearableAssignedMessage(
                     msg.payload as WsWearableAssignedPayload
                  )
                  break
            }
         } catch {
            updateStatus({ lastError: "Failed to parse realtime message" })
         }
      }

      const connectWs = () => {
         if (typeof WebSocket === "undefined") return
         if (
            socket?.readyState === WebSocket.OPEN ||
            socket?.readyState === WebSocket.CONNECTING
         ) {
            return
         }

         authenticatedRef.current = false
         updateStatus({
            connected: false,
            authenticated: false,
            lastError: null,
         })

         socket = new WebSocket(url)
         socket.onopen = () => {
            reconnectAttempt = 0
            updateStatus({ connected: true, lastError: null })
            sendAuth()
            startPingInterval()
         }
         socket.onmessage = handleMessage
         socket.onerror = () =>
            updateStatus({ lastError: "Realtime socket error" })
         socket.onclose = () => {
            clearPingTimer()
            authenticatedRef.current = false
            updateStatus({ connected: false, authenticated: false })
            if (!manuallyClosed) {
               const delay = Math.min(
                  RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttempt,
                  RECONNECT_MAX_DELAY_MS
               )
               reconnectAttempt += 1
               reconnectTimer = setTimeout(connectWs, delay)
            }
         }
      }

      connectWs()

      return () => {
         manuallyClosed = true
         clearReconnectTimer()
         flushFeeds()
         closeSocket()
         authenticatedRef.current = false
         sendFilterRef.current = null
         updateStatus({
            connected: false,
            authenticated: false,
            lastPongAt: null,
         })
      }
   }, [rtsToken, url, dispatch])

   const producersKey = useMemo(() => {
      return producers
         .map(({ entityId, macAddress, customIdentifier }) =>
            customIdentifier === undefined
               ? `${entityId}::${macAddress}`
               : `${entityId}::${macAddress}::${customIdentifier}`
         )
         .sort((a, b) => a.localeCompare(b))
         .join("|")
   }, [producers])

   useEffect(() => {
      producersRef.current = producers
   }, [producers])

   useEffect(() => {
      if (producersKey === lastFilterKeyRef.current) return
      lastFilterKeyRef.current = producersKey

      sourceToIdRef.current.clear()
      for (const {
         entityId,
         macAddress,
         customIdentifier,
      } of producersRef.current) {
         if (customIdentifier === undefined) {
            sourceToIdRef.current.set(normalizeMacAddress(macAddress), entityId)
         } else {
            sourceToIdRef.current.set(
               sourceKey(macAddress, customIdentifier),
               entityId
            )
         }
      }

      sendFilterRef.current?.()
   }, [producersKey])
}
